import { useId } from 'react';
import { EMPTY_FILTERS, filtersToSearch, type OpsFilters } from '@urbanrats/ops-core';
import { navigate, useCtx } from '../lib.ts';

/** datetime-local (interpreted as UTC) ↔ ISO instant. */
const toInput = (iso: string | null) => (iso ? iso.slice(0, 16) : '');
const fromInput = (v: string) => (v ? `${v}:00.000Z` : null);

export function FilterBar({ path }: { path: string }) {
  const { data, filters, search, user } = useCtx();
  const id = useId();
  const extra = Object.fromEntries([...new URLSearchParams(search)].filter(([k]) => !(k in EMPTY_FILTERS)));
  const set = (patch: Partial<OpsFilters>) => navigate(`${path}${filtersToSearch({ ...filters, ...patch }, extra)}`, true);
  const operators = [...new Map(data.tenants.map((t) => [t.operatorId, t.operatorName])).entries()];
  const brands = data.tenants.filter((t) => !filters.operator || t.operatorId === filters.operator);
  const currencies = [...new Set(data.tenants.map((t) => t.currency.code))];
  const cameras = [...new Set(data.rounds.map((r) => r.cameraAlias as string))];
  const sel = (key: keyof OpsFilters, label: string, opts: [string, string][]) => (
    <label className="f-item" htmlFor={`${id}-${key}`}>
      <span>{label}</span>
      <select id={`${id}-${key}`} value={filters[key] ?? ''} onChange={(e) => set({ [key]: e.target.value || null })} data-testid={`filter-${key}`}>
        <option value="">All</option>
        {opts.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <form className="filters" aria-label="Filters" onSubmit={(e) => e.preventDefault()}>
      <label className="f-item" htmlFor={`${id}-from`}>
        <span>From (UTC)</span>
        <input id={`${id}-from`} type="datetime-local" value={toInput(filters.from)} onChange={(e) => set({ from: fromInput(e.target.value) })} data-testid="filter-from" />
      </label>
      <label className="f-item" htmlFor={`${id}-to`}>
        <span>To (UTC)</span>
        <input id={`${id}-to`} type="datetime-local" value={toInput(filters.to)} onChange={(e) => set({ to: fromInput(e.target.value) })} data-testid="filter-to" />
      </label>
      {sel('game', 'Game', data.games.map((g) => [g.gameId, g.title]))}
      {sel('camera', 'Camera', cameras.map((c) => [c, c]))}
      {user.role !== 'PARTNER_ADMIN' && sel('operator', 'Operator', operators)}
      {sel('brand', 'Brand', brands.map((b) => [b.brandId, b.brandName]))}
      {sel('currency', 'Currency', currencies.map((c) => [c, c]))}
      <button type="button" className="btn ghost" onClick={() => navigate(`${path}${filtersToSearch(EMPTY_FILTERS, extra)}`, true)}>
        Clear filters
      </button>
    </form>
  );
}
